import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    } else {
      setSubscription(null);
      setLoading(false);
    }
  }, [user, fetchSubscription]);

  // Check for pending upgrade after returning from Square payment
  useEffect(() => {
    const checkPendingUpgrade = async () => {
      const pendingUpgrade = localStorage.getItem('pendingUpgrade');
      if (pendingUpgrade && user) {
        try {
          const upgrade = JSON.parse(pendingUpgrade);
          // Check if upgrade was recent (within last 30 minutes)
          const upgradeTime = new Date(upgrade.timestamp);
          const now = new Date();
          const timeDiff = (now - upgradeTime) / 1000 / 60; // in minutes
          
          if (timeDiff < 30) {
            // Refresh subscription to get updated plan
            await fetchSubscription();
            console.log('Subscription refreshed after payment redirect');
          }
          // Clear the pending upgrade regardless
          localStorage.removeItem('pendingUpgrade');
        } catch (error) {
          console.error('Error processing pending upgrade:', error);
          localStorage.removeItem('pendingUpgrade');
        }
      }
    };
    
    checkPendingUpgrade();
  }, [user, fetchSubscription]);

  // Refresh subscription every 60 seconds if user has a pending upgrade
  // This catches webhook updates that happen while user is waiting
  useEffect(() => {
    const pendingUpgrade = localStorage.getItem('pendingUpgrade');
    if (!pendingUpgrade || !user) return;

    const interval = setInterval(() => {
      fetchSubscription();
    }, 60000); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [user, fetchSubscription]);

  const isTrialExpired = subscription?.isTrialExpired || false;
  const isActive = subscription?.status === 'active';
  const isCancelled = subscription?.status === 'cancelled';
  const isSubscriptionExpired = subscription?.isSubscriptionExpired || false;
  
  // Check if child limit is reached
  // CRITICAL: Default to unlimited (not 25) to prevent lockout during loading
  const childCount = subscription?.childCount || 0;
  const maxChildren = subscription?.maxChildren || 'unlimited';
  const isChildLimitReached = maxChildren !== 'unlimited' && childCount >= maxChildren;
  const childSlotsRemaining = maxChildren === 'unlimited' ? 'unlimited' : Math.max(0, maxChildren - childCount);
  
  // Can access if: active, cancelled but not expired, trial not expired, OR still loading (no subscription data yet)
  // CRITICAL: Grant access while loading to prevent lockout. Owner/lifetime always allowed.
  const canAccess = !subscription || subscription?.isOwnerAccount ||
    (isActive && !isSubscriptionExpired) ||
    (isCancelled && !isSubscriptionExpired) || 
    (subscription?.status === 'trial' && !isTrialExpired);

  const refreshSubscription = () => {
    fetchSubscription();
  };

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      loading,
      isTrialExpired,
      isActive,
      isCancelled,
      isSubscriptionExpired,
      canAccess,
      isChildLimitReached,
      childCount,
      maxChildren,
      childSlotsRemaining,
      refreshSubscription
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
